import { useEffect, useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { useOsmIsochrone } from '@/api/hooks/useOsmIsochrone';
import type { Costing, IsochroneFeatureCollection } from '@/types/osm';

interface IsochronePanelProps {
  pendingClick: { lng: number; lat: number } | null;
  consumePendingClick: () => void;
  point: { lng: number; lat: number } | null;
  setPoint: (p: { lng: number; lat: number } | null) => void;
  onResult: (fc: IsochroneFeatureCollection | null) => void;
}

const CONTOUR_COLOURS = ['22c55e', '3b82f6', 'f97316', 'ef4444'];

export default function IsochronePanel({
  pendingClick,
  consumePendingClick,
  point,
  setPoint,
  onResult,
}: IsochronePanelProps) {
  const [costing, setCosting] = useState<Costing>('auto');
  const [minutes, setMinutes] = useState('15, 30, 45');
  const mut = useOsmIsochrone();

  useEffect(() => {
    if (!pendingClick) return;
    setPoint({ lng: pendingClick.lng, lat: pendingClick.lat });
    consumePendingClick();
  }, [pendingClick, setPoint, consumePendingClick]);

  async function compute() {
    if (!point) return;
    const mins = minutes
      .split(',')
      .map((s) => parseFloat(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
      .slice(0, 4);
    if (!mins.length) return;
    const contours = mins.map((m, i) => ({ time: m, color: CONTOUR_COLOURS[i] }));
    try {
      const fc = await mut.mutateAsync({
        locations: [{ lat: point.lat, lon: point.lng }],
        costing,
        contours,
        polygons: true,
      });
      onResult(fc);
    } catch {
      onResult(null);
    }
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div>
        <h3 className="text-sm font-semibold">Drive-time isochrones</h3>
        <p className="text-xs text-muted-foreground">
          Click on the map to set the seed point. Up to 4 contours.
        </p>
      </div>

      <Card>
        <CardContent className="p-3 text-xs">
          <div className="text-muted-foreground">Seed point</div>
          {point ? (
            <div className="font-mono">
              {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
            </div>
          ) : (
            <div className="italic text-muted-foreground">Click the map to set</div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Mode</span>
          <Select value={costing} onValueChange={(v) => setCosting(v as Costing)}>
            <SelectTrigger className="h-7 flex-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Car</SelectItem>
              <SelectItem value="truck">Truck</SelectItem>
              <SelectItem value="bicycle">Bicycle</SelectItem>
              <SelectItem value="pedestrian">Walk</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Contour minutes (comma-separated)</span>
          <Input
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            placeholder="15, 30, 45"
            className="h-8 text-xs"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          className="flex-1"
          onClick={() => void compute()}
          disabled={!point || mut.isPending}
        >
          {mut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1 h-3.5 w-3.5" />}
          Compute
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setPoint(null);
            mut.reset();
            onResult(null);
          }}
        >
          Clear
        </Button>
      </div>

      {mut.isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-2 text-xs text-red-800">
            {(mut.error as Error).message}
          </CardContent>
        </Card>
      )}

      {mut.data && (
        <Card>
          <CardContent className="p-2 text-xs">
            <div>{mut.data.features.length} polygon(s) returned</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
